import React, { useEffect, useState, useMemo, useRef } from "react";
import { Loader2, AlertCircle, MessageSquare, Save, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext";
import { widgetConfigApi } from "../services/widgetConfigApi";
import { invalidateChatWidgetConfig } from "../hooks/useChatWidgetConfig";
import { apiErrorMessage } from "../utils/apiErrorMessage";
import CustomSelect from "./CustomSelect";
import ThemeSection from "./WidgetSettings/ThemeSection";
import CopySection from "./WidgetSettings/CopySection";
import FeaturesSection from "./WidgetSettings/FeaturesSection";
import LanguageSection from "./WidgetSettings/LanguageSection";
import PreviewPane from "./WidgetSettings/PreviewPane";
import { stripBlanks } from "./WidgetSettings/stripBlanks";
import { swapDefaultLocale } from "./WidgetSettings/swapDefaultLocale";
import { copyLocaleTabs, localeLabel } from "./WidgetSettings/widgetLocales";

const WidgetSettingsPage = () => {
  const { translate } = useLanguage();
  const { chats, selectedChatId, setSelectedChatId, loading: chatsLoading, chatsLoaded, loadChats } = useChat();
  const { isPlatformOnlySuperAdmin } = useAuth();

  const [settings, setSettings] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // Contextos de página com edições por gravar, que o PageContextsBrowser
  // guarda fora do formulário. O Guardar da página não os leva, portanto o
  // `hasChanges` não os vê: é isto que avisa antes de os perder.
  const [hasUnsavedContexts, setHasUnsavedContexts] = useState(false);

  // Read by handleImported after its await. A closure over selectedChatId would
  // be the stale value from the render that started the import, which is the
  // very thing that needs checking.
  const selectedChatIdRef = useRef(selectedChatId);
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);

  // Load chats for non-super-admins (mirrors ConfigurationsPage)
  useEffect(() => {
    if (!isPlatformOnlySuperAdmin() && !chatsLoaded && !chatsLoading) {
      loadChats();
    }
  }, [isPlatformOnlySuperAdmin, chatsLoaded, chatsLoading, loadChats]);

  // Load settings on chat change
  useEffect(() => {
    if (!selectedChatId) return;
    setIsLoading(true);
    setLoadError(null);
    // Limpos ANTES de pedir os dados do chat novo. O render é
    // `{isLoading && spinner}` seguido de `{formValues && formulário}`, e não um
    // ou outro: sem isto, entre escolher o chat B e os dados de B chegarem, o
    // formulário mostra o copy de A enquanto o chatId já é B — e o diálogo de
    // import, que é destrutivo, lê a contagem de A para uma escrita em B.
    setSettings(null);
    setFormValues(null);
    // A mesma guarda que o handleImported usa, e pela mesma razão: A → B → A
    // depressa deixa três pedidos em voo, sem cancelamento e sem ordem
    // garantida. A resposta de B a chegar em último escrevia o tema, o copy e as
    // features de B numa página cujo chatId já é A. O hasChanges ficava false
    // porque settings e formValues concordavam, nada avisava, e o Guardar
    // seguinte fazia PUT da config inteira de B para dentro de A.
    const requestedChatId = selectedChatId;
    widgetConfigApi
      .get(selectedChatId)
      .then((data) => {
        if (requestedChatId !== selectedChatIdRef.current) return;
        setSettings(data);
        setFormValues(structuredClone(data));
      })
      .catch((err) => {
        console.error("Failed to load widget settings", err);
        if (requestedChatId !== selectedChatIdRef.current) return;
        // Este loadError é renderizado directamente como filho de JSX: sem o
        // desempacotamento do apiErrorMessage, um 422 nesta rota deita a página
        // inteira no error boundary.
        setLoadError(apiErrorMessage(err, err.message));
      })
      .finally(() => {
        // Guardado também: sem isto a resposta atrasada de A apagava o spinner
        // enquanto B ainda estava a carregar, e a página ficava em branco.
        if (requestedChatId !== selectedChatIdRef.current) return;
        setIsLoading(false);
      });
  }, [selectedChatId]);

  // Fechar ou recarregar o separador. O browser mostra o seu próprio texto; o
  // que conta é o evento cancelado.
  useEffect(() => {
    if (!hasUnsavedContexts) return;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedContexts]);

  // Trocar de chat remonta o CopySection (key={selectedChatId}), e os rascunhos
  // dos contextos vão com ele.
  const handleChatChange = (chatId) => {
    if (chatId === selectedChatId) return;
    if (
      hasUnsavedContexts &&
      !window.confirm(
        translate("pageContextsLeaveConfirm") ||
          "Some page contexts have unsaved edits. Switching chat discards them. Continue?"
      )
    ) {
      return;
    }
    setSelectedChatId(chatId);
  };

  // Deep-equal via stringify is intentional: the payload is small (theme/copy/
  // features, three flat blocks), and stringify gives us order-stable
  // comparison without a deepEqual dep. Reach for a real comparator if this
  // ever grows past a few KB or starts including array reorderings that
  // shouldn't count as dirty. It stays small because widgetConfigApi strips
  // `copy.pageContexts` from every response before it reaches this state: with
  // them in, this ran over 3 MB on every keystroke.
  const hasChanges = useMemo(
    () => JSON.stringify(formValues) !== JSON.stringify(settings),
    [formValues, settings],
  );

  // The widget's preview reads the interface language from config.i18n.defaultLocale
  // (WidgetConfig shape) — map the page's flat `locale` field into that block.
  const previewConfig = useMemo(
    () => formValues && { ...formValues, i18n: { defaultLocale: formValues.locale || "pt" } },
    [formValues],
  );

  // O separador de idioma da Copy vive aqui e não dentro do CopySection porque a
  // pré-visualização tem de dizer que idioma mostra, e mostra sempre o por
  // omissão: leva a `copy` de topo e o `i18n.defaultLocale`, e nenhum dos dois
  // muda com o separador. Enquanto ninguém dissesse isso, o ecrã parecia um
  // editor avariado — cinco campos que se escrevem e uma pré-visualização que
  // não mexe. A por separador é o trabalho seguinte.
  const [copyLocale, setCopyLocale] = useState(null);
  const copyTabs = copyLocaleTabs(formValues?.locale, formValues?.locales);
  // Resolvido, e não o escolhido em bruto: tirar um idioma do selector — que está
  // nesta mesma página — deixa o escolhido a apontar para um separador que já não
  // existe, e a legenda não pode nomear um idioma que o ecrã não mostra.
  const activeCopyLocale = copyTabs.includes(copyLocale) ? copyLocale : copyTabs[0];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // "Ausente" quer dizer duas coisas diferentes de cada lado deste PUT.
      // Aqui quer dizer APAGADO: o stripBlanks tira do mapa um idioma que ficou
      // sem campos, e com o último a sair o `translations` desaparece do
      // payload. No backend quer dizer NÃO MENCIONADO: a guarda do PUT repõe o
      // mapa guardado, para que um backoffice antigo — que nunca mandou a chave
      // — não apague os mercados todos num save. Sem esta linha a guarda não
      // distingue as duas, e apagar a última tradução não pega: o operador
      // limpa o separador espanhol, grava, vê o toast de sucesso, e os textos
      // voltam ao ecrã. O nulo explícito é a diferença — o `translations` é
      // `dict | None`, portanto a chave presente chega ao backend e a guarda
      // sai da frente. Não o apagues por parecer redundante.
      const copy = stripBlanks(formValues.copy || {});
      if (settings?.copy?.translations && copy.translations === undefined) {
        copy.translations = null;
      }
      const payload = {
        theme: formValues.theme || {},
        copy,
        features: formValues.features || {},
      };
      // Guard locale in the PUT payload: only include it if the loaded settings had it.
      // This ensures compatibility with backends that don't yet support locale.
      if (settings?.locale !== undefined) {
        payload.locale = formValues.locale || "pt";
      }
      if (settings?.locales !== undefined) {
        payload.locales = formValues.locales || [];
      }
      const updated = await widgetConfigApi.update(selectedChatId, payload);
      setSettings(updated);
      setFormValues(structuredClone(updated));
      // A transcricao das Conversas guarda o tema de cada chat numa cache ao
      // nivel do modulo, que dura a sessao. Sem esta linha, quem acabou de
      // mudar as cores aqui continuava a ve-las antigas la ao lado.
      invalidateChatWidgetConfig(selectedChatId);
      toast.success(translate("widgetSettingsSaved") || "Widget settings saved");
    } catch (err) {
      console.error("Save failed", err);
      toast.error(
        apiErrorMessage(err, translate("widgetSettingsSaveFailed") || "Save failed")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setFormValues(structuredClone(settings));
  };

  // The import wrote on the server, outside this form, so the page has to catch
  // up. The contexts themselves are not in this form, but a file that carries
  // `welcomeQuickReplies` also replaces the default replies, and those are.
  // BOTH settings and formValues are replaced: setting only `settings` would
  // leave formValues holding the pre-import copy, and the page would go dirty
  // the instant the import succeeded, with a Save button that would put the
  // old default replies straight back.
  //
  // Takes the chat it imported into rather than reading selectedChatId: the
  // picker stays enabled during an import (it is only disabled while chats or
  // settings are loading), and CopySection unmounting on a chat switch does not
  // cancel the request that is already in flight. Without the guard below, an
  // import into A that resolves after the operator moved to B writes A's whole
  // widget config — theme, copy, features — onto the page under B's name, with
  // hasChanges false so nothing warns, and the next Save PUTs it into B.
  const handleImported = async (importedChatId) => {
    // Invalidated regardless of where the page has moved to: the transcript's
    // per-module cache for THAT chat is stale either way.
    invalidateChatWidgetConfig(importedChatId);
    try {
      const data = await widgetConfigApi.get(importedChatId);
      if (importedChatId !== selectedChatIdRef.current) return;
      setSettings(data);
      setFormValues(structuredClone(data));
    } catch (err) {
      console.error("Refetch after import failed", err);
      // O import ENTROU; o que falhou foi a releitura. Deixar o formulário com a
      // configuração PRÉ-import é a pior das saídas: o hasChanges fica false,
      // nada avisa, e o Guardar seguinte faz PUT das respostas por omissão
      // antigas por cima das que o ficheiro trouxe, com um toast a dizer que
      // gravou. Um ecrã de erro não tem de onde gravar, e é isso que o torna
      // seguro.
      //
      // A guarda é a mesma do caminho de sucesso, e está aqui pela mesma razão:
      // um import de 300 contextos demora, o operador troca de chat enquanto ele
      // corre, e sem ela o get(A) a falhar limpava o formulário de B, que já
      // tinha carregado bem, para pôr no lugar um erro sobre um import que
      // aconteceu noutro chat.
      if (importedChatId === selectedChatIdRef.current) {
        setSettings(null);
        setFormValues(null);
        setLoadError(
          translate("widgetSettingsReloadFailed") ||
            "Imported, but the page could not reload. Refresh to see the new contexts."
        );
      }
      // Propagado de propósito: sem isto o `await onImported(...)` do
      // PageContextsIO resolve na mesma, o diálogo fecha e o toast de sucesso
      // aterra por cima de um contador que ainda mostra os números antigos.
      throw err;
    }
  };

  // Canonical defaults live in ai-agent-backend:
  // src/app/schemas/widget_config.py (FeaturesBlock).
  // Keep these in sync; or, if drift becomes a real risk, swap this for a
  // GET /widget-config/{chat_id}/defaults call (not yet wired backend-side).
  const FEATURE_DEFAULTS = {
    fileUpload: true,
    audioMessages: false,
    productCardDelivery: false,
    productCardTagBadges: false,
    productCardDiscountBadge: false,
    productCardHoverImage: false,
    productCardViewCta: false,
    cardLinksSameTab: false,
  };

  const handleResetDefaults = () => {
    toast(
      (t) => (
        <div className="flex flex-col gap-3">
          <p className="text-sm" style={{ color: "var(--color-text-primary)" }}>
            {translate("confirmResetDefaults") || "Reset all widget settings to defaults?"}
          </p>
          {/* Duas frases, e as duas são precisas aqui. A primeira porque o
              reset apaga MESMO as traduções — o save passou a mandar
              `translations: null`, e antes disso elas sobreviviam por acidente
              da guarda do PUT, não por desenho. "Repor tudo" quer dizer tudo, e
              o que está em jogo podem ser cinco mercados que nós escrevemos.
              A segunda porque o reset já não toca nos contextos de página:
              vivem nas tabelas e gravam-se pelas rotas próprias, e o PUT desta
              página ignora-os; no dev anterior a este PR apagava-os. Com uma
              excepção nomeada e a outra não, quem lê assume que a que falta
              também é excepção. */}
          <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
            {translate("confirmResetDefaultsClearsTranslations") ||
              "The copy is cleared in every language, translations included."}{" "}
            {translate("confirmResetDefaultsKeepsPageContexts") || "Page contexts stay as they are."}
          </p>
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="btn-secondary text-xs"
              style={{ padding: "4px 10px" }}
            >
              {translate("cancel") || "Cancel"}
            </button>
            <button
              onClick={() => {
                setFormValues({
                  theme: {},
                  copy: {},
                  features: { ...FEATURE_DEFAULTS },
                  locale: "pt",
                  // Dito e não deixado de fora: sem ele o reset só ficava certo
                  // por causa do `|| []` de quem o lê a jusante.
                  locales: [],
                });
                toast.dismiss(t.id);
              }}
              className="btn-primary text-xs"
              style={{ padding: "4px 10px" }}
            >
              {translate("resetToDefaults") || "Reset to defaults"}
            </button>
          </div>
        </div>
      ),
      { duration: 10000 },
    );
  };

  const activeChatsCount = chats ? chats.filter((c) => c.is_active).length : 0;

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-start justify-between pb-5" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
            {translate("widgetSettings") || "Widget"}
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            {translate("widgetSettingsDescription") || "Customise your chat widget's appearance and behaviour."}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {formValues && (
            <button
              onClick={handleResetDefaults}
              className="btn-secondary text-xs"
              style={{ padding: "6px 12px" }}
              title={translate("resetToDefaultsTooltip") || "Restore all widget settings to defaults"}
            >
              {translate("resetToDefaults") || "Reset to defaults"}
            </button>
          )}
          {!isPlatformOnlySuperAdmin() && activeChatsCount > 1 && (
            <CustomSelect
              value={selectedChatId || ""}
              onChange={handleChatChange}
              label={translate("selectChat") || "Select Chat"}
              icon={MessageSquare}
              disabled={chatsLoading || isLoading}
              className="w-56"
              options={chats
                .filter((c) => c.is_active)
                .sort((a, b) => (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0))
                .map((c) => ({ value: c.id, label: c.name }))
              }
            />
          )}
        </div>
      </div>

      {loadError && (
        <div className="px-4 py-3 rounded-lg flex items-center gap-2 text-sm"
          style={{ backgroundColor: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", color: "var(--color-error)" }}>
          <AlertCircle size={14} />
          {loadError}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-3 px-5 py-4">
          <Loader2 size={18} className="animate-spin" style={{ color: "var(--color-mint)" }} />
          <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            {translate("loadingConfigurations") || "Loading..."}
          </span>
        </div>
      )}

      {formValues && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-4">
            <ThemeSection
              theme={formValues.theme}
              baseline={settings?.theme}
              onChange={(theme) => setFormValues((prev) => ({ ...prev, theme }))}
            />
            {/* Keyed on the chat so switching remounts the whole section.
                CopySection's children hold per-chat UI state that props alone
                cannot reset — which context card is expanded, and each patterns
                textarea's raw buffer. A remount clears both declaratively;
                reconciling them from inside proved fragile under StrictMode. */}
            <CopySection
              key={selectedChatId}
              copy={formValues.copy}
              baseline={settings?.copy}
              locale={formValues.locale}
              locales={formValues.locales}
              activeLocale={activeCopyLocale}
              onActiveLocaleChange={setCopyLocale}
              onChange={(copy) => setFormValues((prev) => ({ ...prev, copy }))}
              chatId={selectedChatId}
              // O import substitui todos os contextos, portanto também espera
              // pelos rascunhos dos cartões e não só pelo formulário.
              hasUnsavedChanges={hasChanges || hasUnsavedContexts}
              onImported={handleImported}
              onUnsavedContextsChange={setHasUnsavedContexts}
            />
            <FeaturesSection
              features={formValues.features}
              baseline={settings?.features}
              onChange={(features) => setFormValues((prev) => ({ ...prev, features }))}
            />
            {settings?.locale !== undefined && (
              <LanguageSection
                locale={formValues.locale}
                locales={formValues.locales}
                baseline={settings?.locale}
                baselineLocales={settings?.locales}
                // Não é só escrever o `locale`: trocar o idioma por omissão
                // move o mercado antigo para os outros e leva a copy com ele.
                // Ver swapDefaultLocale — precisa do formValues inteiro, que é
                // por isso que a transacção vive aqui e não no LanguageSection.
                onChange={(locale) => setFormValues((prev) => swapDefaultLocale(prev, locale))}
                onChangeLocales={(locales) => setFormValues((prev) => ({ ...prev, locales }))}
              />
            )}
            {hasChanges && (
              <div className="sticky bottom-0 px-6 py-4 flex items-center justify-between rounded-xl"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                }}>
                <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("unsavedChanges") || "You have unsaved changes"}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={handleDiscard} className="btn-secondary" style={{ padding: "6px 14px" }}>
                    <X size={14} />
                    {translate("discard") || "Discard"}
                  </button>
                  <button onClick={handleSave} disabled={isSaving} className="btn-primary" style={{ padding: "6px 14px" }}>
                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {isSaving ? translate("saving") || "Saving..." : translate("saveChanges") || "Save Changes"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-6 lg:self-start space-y-2">
            {/* Só com mais do que um idioma: um tenant de um mercado não tem
                separadores nenhuns e a legenda não teria nada a esclarecer. */}
            {copyTabs.length > 1 && (
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                {activeCopyLocale === copyTabs[0]
                  ? translate("previewLanguage", { language: localeLabel(copyTabs[0]) })
                  : translate("previewLanguageOtherTab", {
                      language: localeLabel(copyTabs[0]),
                      tab: localeLabel(activeCopyLocale),
                    })}
              </p>
            )}
            <PreviewPane config={previewConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetSettingsPage;
